import { ApexOptions } from 'apexcharts';
import React from 'react';
import Chart from 'react-apexcharts';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';
import { Box, Typography } from '@mui/material';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';

// note, that tickAmount has not to be set when rendering the component as
// this is done in render...Action below! It happens within the options that
// are passed as an argument to the component.
// the tickAmound is set here anyway as we need it in widgetData
export interface PolarchartDisplayProps {
  options?: ApexOptions;
  series: number[];
  labels: string[];
  width?: number;
  title?: string;
  tickAmount?: number;
}

// no tickAmount here, see komment in props definition above
export const PolarchartDisplay: React.FC<PolarchartDisplayProps> = ({
  options,
  series,
  labels,
  title,
}) => {
  const standardWidth = 500;

  return (
    <div>
      {title !== undefined && title.trim() !== '' && (
        <Typography
          variant="h5"
          style={{
            textAlign: 'center',
            fontWeight: 'bold',
            width: standardWidth,
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ marginBottom: '10px', marginTop: '10px' }}>
        <Chart
          options={{ ...options, labels: labels, series: series }}
          series={series}
          type="polarArea"
          width={standardWidth}
          id="polarchart"
          key="polarchart"
        />
      </Box>
    </div>
  );
};

export interface PolarchartDisplayComponent extends BaseComponent {
  type: 'polarchart';
  props: PolarchartDisplayProps;
}

export const defaultPolarChartOptions: ApexOptions = {
  stroke: {
    show: false,
  },
  colors: [
    '#FABD01',
    '#a867e6',
    '#F08144',
    '#4D70EA',
    '#FFE699',
    '#FFB2A0',
    '#4199AC',
    '#78934B',
  ],
  legend: {
    show: true,
    fontWeight: 400,
    fontSize: '14',
    position: 'bottom',
    itemMargin: {
      horizontal: 10,
      vertical: 5,
    },
  },
  fill: {
    opacity: 0.8,
    colors: [
      '#FABD01',
      '#a867e6',
      '#F08144',
      '#4D70EA',
      '#FFE699',
      '#FFB2A0',
      '#4199AC',
      '#78934B',
    ],
  },
  plotOptions: {
    area: {
      fillTo: 'end',
    },
    polarArea: {
      rings: {
        strokeWidth: 1,
        strokeColor: '#BEBEBE',
      },
      spokes: {
        strokeWidth: 1,
        connectorColors: '#BEBEBE',
      },
    },
  },
  yaxis: {
    show: false,
    max: 10,
    min: 1,
    tickAmount: 10,
  },
  grid: {
    position: 'front',
    yaxis: {
      lines: {
        show: false,
      },
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        legend: {
          position: 'bottom',
        },
      },
    },
  ],
};

export const renderPolarchartDisplayAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    //console.log('display');
    //console.log(payload);

    let polaroptions = payload.options;

    if (polaroptions === undefined || polaroptions === '') {
      polaroptions = defaultPolarChartOptions;
    }

    // max number of segments (default is 10)
    if (payload.tickAmount) {
      polaroptions.yaxis.tickAmount = +payload.tickAmount;
      polaroptions.yaxis.max = +payload.tickAmount;
    }

    // if series payload is set, take that, if not, empty series
    let series: number[] = payload.series ?? [];
    let labels: string[] = [];

    const getLabelsFrom = payload.getLabelsFrom;
    const getSeriesFrom = payload.getSeriesFrom;

    series = getSeriesFrom
      ? series.concat(context.userData[getSeriesFrom])
      : series.concat([]);

    // in v2 of PolarChart State Entry in Builder, there might be two new variables available,
    if (getLabelsFrom && getLabelsFrom.trim('') !== '') {
      // auto-generation of labels + series
      labels = context.userData[getLabelsFrom].map((labelId: string) =>
        translateWithFallback(intl, labelId)
      );
    } else {
      // if not autogenerated, then labels are explicitely defined
      labels = payload.labels.map((labelId: string) =>
        translateWithFallback(intl, labelId)
      );
    }

    // var names are in the array in context.userData[actionMetadata.action.payload.getValueFrom]
    // iterate this array and get the vars
    // if there are values, override and delete any potentially passed series input
    if (
      Array.isArray(payload.getValuesFrom) &&
      payload.getValuesFrom.length > 0 &&
      payload.getValuesFrom.every(
        (varname: string) =>
          varname.trim() !== '' && varname !== undefined && varname !== null
      )
    ) {
      payload.getValuesFrom.forEach((varname: string) => {
        if (
          context.userData[varname] !== undefined &&
          context.userData[varname] !== null
        ) {
          series.push(context.userData[varname]);
        }
      });
      series = series.flat();
    }

    //console.log('PolarchartDisplay series ' + series);
    //console.log('PolarchartDisplay labels ' + labels);
    // push widget
    newContext.widgetData.push({
      type: 'polarchart',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        options: polaroptions as ApexOptions,
        series: series,
        labels: labels,
        width: payload.width > 650 ? 650 : payload.width,
        title: translateWithFallback(intl, payload.title),
        tickAmount: payload.tickAmount ?? 10,
      },
    });

    return newContext;
  });
};
